import { useState } from 'react';
import type { ReactNode } from 'react';
import type { UpdateCarInput } from '@fleet/shared';
import { Button, Input } from '../../design-system';
import { Dropdown } from '../../components/ui/Dropdown';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { PencilIcon } from '../../components/ui/Icons';
import { Panel } from '../../components/ui/Panel';

type FieldType = 'text' | 'date' | 'euro' | 'select' | 'textarea';

export interface PanelField {
  label: string;
  /** Editable field key; omit for a read-only computed row (e.g. restwaarde). */
  key?: keyof UpdateCarInput;
  type?: FieldType;
  /** Read-only rendering of the current value. */
  display: ReactNode;
  /** Current value as a plain string, used to seed the input. */
  raw?: string;
  options?: { value: string; label: string }[];
  placeholder?: string;
  /** Helper text under the input, edit mode only — never in the read-only row. */
  hint?: string;
  /** Draw a hairline after this row, to lift the answer above its inputs. */
  scheiding?: boolean;
}

/**
 * A titled card of the car's data: label/value rows with the values aligned on a
 * shared right-hand column, and a "Bewerken" toggle that turns the editable ones
 * into inputs and saves them in a single patch. Computed rows (no `key`) stay
 * read-only while editing.
 *
 * A card with at most one `+` next to the pencil, and that `+` always belongs to
 * the heading above it. It used to sit on Autogegevens meaning "add an odometer
 * reading", which nothing about that heading suggested — the reading has its own
 * card now.
 */
export function CarInfoPanel({
  title,
  fields,
  onSave,
  busy,
  headerAction,
  extra,
  extraBoven,
  anker,
  oplicht,
}: {
  title: string;
  fields: PanelField[];
  onSave: (patch: Record<string, string | null>) => void;
  busy?: boolean;
  /** Scroll target for a checklist step that points at this card. */
  anker?: string;
  /**
   * Draw the card as the one just jumped to. Without it a scroll on a wide screen
   * lands you next to five identical cards with nothing saying which one you asked
   * for; the page clears it again after a moment.
   */
  oplicht?: boolean;
  /** Extra control next to "Bewerken", hidden while editing. */
  headerAction?: ReactNode;
  /**
   * Content below the fields with its own controls — the dated fixed costs.
   * Hidden while editing, like `headerAction`: those amounts are not part of
   * this section's patch and two save buttons in one place read as one.
   */
  extra?: ReactNode;
  /** Same, but above the fields — for a section whose whole point is the amount. */
  extraBoven?: ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Een sectie zonder bewerkbare velden krijgt geen potlood: dat opende een
  // bewerkmodus met alleen een Opslaan-knop en niets om te wijzigen.
  const bewerkbaar = fields.some((f) => f.key);

  function startEdit() {
    const init: Record<string, string> = {};
    for (const f of fields) if (f.key) init[f.key] = f.raw ?? '';
    setDraft(init);
    setEditing(true);
  }

  function save() {
    const patch: Record<string, string | null> = {};
    for (const f of fields) {
      if (!f.key) continue;
      const next = draft[f.key] ?? '';
      if (next !== (f.raw ?? '')) patch[f.key] = next === '' ? null : next;
    }
    if (Object.keys(patch).length > 0) onSave(patch);
    setEditing(false);
  }

  return (
    <Panel
      id={anker}
      style={{
        padding: '20px 24px',
        // Alleen de rand, geen achtergrond: de kaart blijft leesbaar zoals hij is.
        // Als los object, want `border: undefined` zou de rand van `Card` wissen.
        ...(oplicht
          ? { border: '1px solid #B0543B', boxShadow: '0 0 0 3px rgba(176,84,59,0.14)' }
          : {}),
        transition: 'border-color .25s ease, box-shadow .25s ease',
      }}
    >
      {/* Alleen icoonknoppen in de kop. Annuleren/Opslaan staan onderaan: naast een
          kopje als "WEGENBELASTING" passen twee tekstknoppen niet in een smalle
          kaart en liepen ze buiten de rand. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          minHeight: 30,
        }}
      >
        <Eyebrow color="#2A6085">{title}</Eyebrow>
        {!editing && (headerAction || bewerkbaar) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {headerAction}
            {bewerkbaar && (
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                title="Bewerken"
                aria-label="Bewerken"
                onClick={startEdit}
              >
                <PencilIcon />
              </Button>
            )}
          </div>
        )}
      </div>

      {!editing && extraBoven && <div style={{ marginTop: 14 }}>{extraBoven}</div>}

      <div
        style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: editing ? 12 : 10 }}
      >
        {fields.map((f) => {
          const editable = editing && f.key;
          if (!editable) {
            return (
              <div key={f.label}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 12.5, color: '#888888' }}>{f.label}</span>
                  <span
                    style={{ fontSize: 14, color: '#111111', fontWeight: 600, textAlign: 'right' }}
                  >
                    {f.display}
                  </span>
                </div>
                {f.scheiding && <div style={{ borderBottom: '1px solid #F0E8DC', marginTop: 9 }} />}
              </div>
            );
          }
          const key = f.key as string;
          const set = (v: string) => setDraft((d) => ({ ...d, [key]: v }));
          if (f.type === 'select') {
            return (
              <Dropdown
                key={f.label}
                label={f.label}
                block
                value={draft[key] ?? ''}
                onChange={set}
                options={f.options ?? []}
              />
            );
          }
          if (f.type === 'textarea') {
            return (
              <label key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#444444' }}>{f.label}</span>
                <textarea
                  value={draft[key] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => set(e.target.value)}
                  rows={3}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 14,
                    padding: '8px 10px',
                    border: '1px solid #C9BCA9',
                    borderRadius: 4,
                    background: '#FBF5EE',
                    color: '#111111',
                    resize: 'vertical',
                  }}
                />
              </label>
            );
          }
          return (
            <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Input
                label={f.label}
                type={f.type === 'date' ? 'date' : 'text'}
                value={draft[key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => set(e.target.value)}
              />
              {f.hint && <span style={{ fontSize: 11.5, color: '#888888' }}>{f.hint}</span>}
            </div>
          );
        })}
      </div>

      {!editing && extra && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #E3D9CC' }}>{extra}</div>
      )}

      {editing && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px solid #E3D9CC',
            flexWrap: 'wrap',
          }}
        >
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
            Annuleren
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={busy}>
            Opslaan
          </Button>
        </div>
      )}
    </Panel>
  );
}
